/**
 * Pure helpers for Design Calendar offline (cache + mutation queue).
 * No IndexedDB here — keep unit-testable.
 */

export function isBrowserOnline(
  nav = typeof navigator !== "undefined" ? navigator : null,
) {
  if (!nav || typeof nav.onLine !== "boolean") return true;
  return nav.onLine;
}

export function shouldQueueOfflineSave({
  online,
  offlineEnabled,
  networkError = false,
} = {}) {
  if (!offlineEnabled) return false;
  if (!online) return true;
  return Boolean(networkError);
}

export function isNetworkError(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
    return true;
  }
  if (!error.response && error.request) return true;
  return false;
}

/** Ids temporales negativos para creates offline. */
export function nextTempId(existingIds = []) {
  let min = 0;
  for (const raw of existingIds) {
    const n = Number(raw);
    if (Number.isInteger(n) && n < min) min = n;
  }
  return min - 1;
}

export function isTempId(id) {
  const n = Number(id);
  return Number.isInteger(n) && n < 0;
}

/**
 * Aplica ops pendientes sobre un snapshot { notas, acciones, clientes, canEdit }.
 */
export function applyPendingOpsToSnapshot(snapshot, pendingOps = []) {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const next = {
    ...snapshot,
    notas: Array.isArray(snapshot.notas) ? [...snapshot.notas] : [],
    acciones: Array.isArray(snapshot.acciones) ? [...snapshot.acciones] : [],
    clientes: Array.isArray(snapshot.clientes)
      ? snapshot.clientes.map((c) => ({ ...c }))
      : snapshot.clientes,
  };

  for (const op of pendingOps || []) {
    if (!op || !op.type) continue;
    switch (op.type) {
      case "nota.create": {
        const row = { ...op.payload, Id: op.tempId };
        next.notas.push(row);
        break;
      }
      case "nota.update": {
        next.notas = next.notas.map((row) =>
          Number(row.Id) === Number(op.id) ? { ...row, ...op.payload } : row,
        );
        break;
      }
      case "nota.delete": {
        next.notas = next.notas.filter(
          (row) => Number(row.Id) !== Number(op.id),
        );
        break;
      }
      case "accion.create": {
        const row = { ...op.payload, Id: op.tempId };
        next.acciones.push(row);
        break;
      }
      case "accion.update": {
        next.acciones = next.acciones.map((row) =>
          Number(row.Id) === Number(op.id) ? { ...row, ...op.payload } : row,
        );
        break;
      }
      case "accion.delete": {
        next.acciones = next.acciones.filter(
          (row) => Number(row.Id) !== Number(op.id),
        );
        break;
      }
      case "cliente.update": {
        if (!Array.isArray(next.clientes)) break;
        next.clientes = next.clientes.map((row) =>
          row.id === op.id ? { ...row, ...op.payload } : row,
        );
        break;
      }
      default:
        break;
    }
  }
  return next;
}

/** Remapea ids temporales en ops posteriores tras un create exitoso. */
export function remapTempIdInOps(ops, tempId, realId) {
  const from = Number(tempId);
  const to = Number(realId);
  if (!Number.isInteger(from) || !Number.isInteger(to)) return ops || [];
  return (ops || []).map((op) => {
    if (!op) return op;
    if (Number(op.id) === from) return { ...op, id: to };
    if (Number(op.tempId) === from) return { ...op, tempId: to };
    return op;
  });
}

export function buildYearSnapshot(data) {
  return {
    notas: Array.isArray(data?.notas) ? data.notas : [],
    acciones: Array.isArray(data?.acciones) ? data.acciones : [],
    clientes: Array.isArray(data?.clientes) ? data.clientes : [],
    canEdit: Boolean(data?.canEdit),
    anio: data?.anio ?? null,
    savedAt: new Date().toISOString(),
  };
}
